import React, { useState, useEffect, useRef } from 'react'
import { Pause, Play, Volume2, VolumeX } from 'lucide-react'

const demotab = [
  {
    tabname: 'AI Chat',
    tabvalue: 'ai-chat',
    src: 'https://assets.ai-pro.org/assets/homepage/demo-videos/AI%20Chat.webm',
    isImage: false,
    demoText: 'aichat',
  },
  {
    tabname: 'Image Generation',
    tabvalue: 'image-generation',
    src: 'https://assets.ai-pro.org/assets/homepage/demo-videos/Image%20Gen-demo.webm',
    isImage: false,
    demoText: 'image generation',
  },
  {
    tabname: 'AI PDF Reader',
    tabvalue: 'ai-pdf-reader',
    src: 'https://assets.ai-pro.org/assets/homepage/demo-videos/AI%20PDF.webm',
    isImage: false,
    demoText: 'ai pdf reader',
  },
  {
    tabname: 'Video Generation',
    tabvalue: 'video-generation',
    src: 'https://assets.ai-pro.org/assets/homepage/demo-videos/Video%20Gen-demo.webm',
    isImage: false,
    demoText: 'video generation',
  },
  {
    tabname: 'Audio Generation',
    tabvalue: 'audio-generation',
    src: 'https://assets.ai-pro.org/assets/homepage/demo-videos/Audio%20Gen-demo.webm',
    isImage: false,
    demoText: 'audio generation',
  },
]

export default function DemoTab() {
  const [activeTab, setActiveTab] = useState('ai-chat')
  const [mediaSrc, setMediaSrc] = useState(null)
  const [isInView, setIsInView] = useState(false)
  const [isPlaying, setIsPlaying] = useState(true)
  const [isMuted, setIsMuted] = useState(true)
  const containerRef = useRef(null)
  const videoRef = useRef(null)
  const isMutedRef = useRef(true)
  const activeDemo = demotab.find((tab) => tab.tabvalue === activeTab) ?? demotab[0]

  useEffect(() => {
    isMutedRef.current = isMuted
  }, [isMuted])

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true)
          observer.disconnect()
        }
      },
      { rootMargin: '200px' }
    )

    if (containerRef.current) {
      observer.observe(containerRef.current)
    }

    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!isInView) return

    setMediaSrc(activeDemo.src)
  }, [activeTab, isInView, activeDemo])

  useEffect(() => {
    const video = videoRef.current
    if (!video || !mediaSrc) return

    const playVideo = async () => {
      const shouldMute = isMutedRef.current
      video.muted = shouldMute
      setIsPlaying(true)

      try {
        await video.play()
      } catch {
        if (!shouldMute) {
          video.muted = true
          setIsMuted(true)
          try {
            await video.play()
          } catch {
            setIsPlaying(false)
          }
        } else {
          setIsPlaying(false)
        }
      }
    }

    if (video.readyState >= 2) {
      playVideo()
    } else {
      video.addEventListener('loadeddata', playVideo, { once: true })
      return () => video.removeEventListener('loadeddata', playVideo)
    }
  }, [mediaSrc])

  const togglePlay = () => {
    const video = videoRef.current
    if (!video) return

    if (video.paused) {
      video.play()
    } else {
      video.pause()
    }
  }

  const toggleMute = () => {
    const video = videoRef.current
    if (!video) return

    video.muted = !video.muted
    setIsMuted(video.muted)
  }

  return (
    <div className='lg:px-0 px-2'>
      <div id="demo-tab" className="flex flex-wrap justify-center gap-2 lg:flex-nowrap lg:mt-[42px]">
        {demotab.map((tab) => (
          <div
            key={tab.tabvalue}
            onClick={() => setActiveTab(tab.tabvalue)}
            className={`
              rounded-full cursor-pointer transition-all duration-300
              leading-none text-[16px] px-[20px] py-[10px]
              border w-fit
              ${
                activeTab === tab.tabvalue
                  ? 'bg-[#3073D5] text-white border-[#3073D5]'
                  : 'bg-[#FCFCFC] border-[#DDDDDD] hover:bg-[#3073D5] hover:text-white'
              }
            `}
          >
            {tab.tabname}
          </div>
        ))}
      </div>

      <div ref={containerRef}>
        <div className='flex flex-col justify-center rounded-[20px] lg:mt-[30px] mt-[20px]'>
          {!mediaSrc ? (
            <div className='w-full lg:min-h-[621px] min-h-[220px] rounded-[10px] bg-white/40 animate-pulse' aria-hidden="true" />
          ) : (
            <div className="relative w-full lg:mb-[90px] mb-[20px]">
              <video
                ref={videoRef}
                key={mediaSrc}
                src={mediaSrc}
                autoPlay
                loop
                muted={isMuted}
                playsInline
                preload="auto"
                className="w-full object-contain rounded-[10px] shadow-lg"
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
              />
              <div className="absolute bottom-3 right-3 flex gap-2">
                <button
                  type="button"
                  onClick={togglePlay}
                  aria-label={isPlaying ? 'Pause video' : 'Play video'}
                  className="flex items-center justify-center w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors"
                >
                  {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                </button>
                <button
                  type="button"
                  onClick={toggleMute}
                  aria-label={isMuted ? 'Unmute video' : 'Mute video'}
                  className="flex items-center justify-center w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors"
                >
                  {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
